import type { ReactNode } from 'react'
import { DefaultLayout } from '@/shared/ui/DefaultLayout'
import { ChevronRightIcon } from '@/shared/ui/icons/ChevronRightIcon'
import { ApiError } from '@/shared/lib/http'
import { PC_FLOW_STEPS, type PcFlowKey } from '@/public/features/org/constants/orgNew'

/** 시안의 Button/Solid/Primay(56px) — 공용 `buttonStyles` 에는 이 크기가 없어 FilterModalPc 와 같은 방식으로 적었다. */
export const PC_PRIMARY_BUTTON_CLASS =
  'ui-pressable flex h-14 min-h-12 shrink-0 cursor-pointer items-center justify-center gap-2.5 rounded-2xl border-0 bg-violet-500 px-4 py-3 text-body-lg font-semibold text-white-50 disabled:cursor-not-allowed disabled:bg-white-300 disabled:text-white-500'

/** 시안의 Button/Outline/Assistive(56px). */
export const PC_ASSISTIVE_BUTTON_CLASS =
  'ui-pressable flex h-14 min-h-12 shrink-0 cursor-pointer items-center justify-center gap-2 rounded-2xl border border-white-400 bg-white-50 px-[22px] py-3 text-body-lg text-black-600'

export function OrgNewPcShell({
  title,
  active,
  error,
  footer,
  children,
}: {
  title: string
  active: PcFlowKey
  error: unknown
  footer: ReactNode
  children: ReactNode
}) {
  return (
    <DefaultLayout tree="pc" className="[--body-w:1000px]">
      <div className="layout-body flex flex-col gap-6 pt-8 pb-[60px]">
        <div className="flex items-center gap-6">
          <p className="m-0 min-w-px flex-1 text-headline-xs font-semibold tracking-[-0.1px] text-black-800">
            {title}
          </p>
          <StepBreadcrumb active={active} />
        </div>

        {children}

        {error !== null && error !== undefined ? (
          <p className="m-0 text-body-sm text-[#f54550]" role="alert">
            <b>등록하지 못했습니다.</b>{' '}
            {error instanceof ApiError && error.detail.length > 0
              ? error.detail.join(' · ')
              : '잠시 후 다시 시도해 주세요.'}
          </p>
        ) : null}

        <div className="flex items-center justify-end gap-2 py-2">{footer}</div>
      </div>
    </DefaultLayout>
  )
}

function StepBreadcrumb({ active }: { active: PcFlowKey }) {
  return (
    <nav className="flex items-center justify-center gap-2" aria-label="기업 등록 단계">
      {PC_FLOW_STEPS.map((s, i) => (
        <span key={s.key} className="flex items-center gap-2">
          {i > 0 ? (
            <span className="flex items-center text-black-100" aria-hidden="true">
              <ChevronRightIcon size={16} />
            </span>
          ) : null}
          <span
            className={
              s.key === active
                ? 'text-body-sm font-bold text-violet-600'
                : 'text-body-sm font-medium text-black-100'
            }
            {...(s.key === active ? { 'aria-current': 'step' as const } : {})}
          >
            {s.label}
          </span>
        </span>
      ))}
    </nav>
  )
}

/** 시안의 여러 줄 Textfield — 높이 180, 라벨 16, 안쪽 여백 14. */
export function PcTextarea({
  label,
  required = false,
  placeholder,
  value,
  onChange,
  maxLength,
  count,
}: {
  label: string
  required?: boolean
  placeholder: string
  value: string
  onChange: (v: string) => void
  maxLength: number
  count?: { current: number; max: number }
}) {
  return (
    <div className="flex w-full flex-col gap-[6px]">
      <label className="flex flex-col gap-[6px]">
        <span className="flex items-start justify-start gap-1 text-body-md font-normal text-black-600">
          <span>{label}</span>
          {required ? (
            <span aria-hidden="true" className="mt-0.5 h-1 w-1 shrink-0 rounded-full bg-[#f54550]" />
          ) : null}
        </span>
        <textarea
          className="h-[180px] w-full resize-none rounded-[14px] border border-white-400 bg-white-50 p-[14px] font-[inherit] text-body-lg text-black-500 placeholder:text-black-100"
          value={value}
          placeholder={placeholder}
          maxLength={maxLength}
          onChange={(e) => onChange(e.target.value)}
        />
      </label>
      {count !== undefined ? (
        <p className="m-0 flex items-center justify-end gap-0.5 text-body-md font-medium whitespace-nowrap">
          <span className="text-right text-violet-900">{count.current}</span>
          <span className="text-[#989898]">{`/${count.max}`}</span>
        </p>
      ) : null}
    </div>
  )
}

/** 시안의 2열 입력 묶음 — 열 폭 490, 열 간격 20. */
export function PcFieldRow({ children }: { children: ReactNode }) {
  return (
    // `w-full` 이 없으면 `items-start` 인 부모 안에서 내용 폭으로 줄어 열이 490 이 되지 않는다(실측).
    <div className="flex w-full items-start gap-5 [&>*]:min-w-px [&>*]:flex-1 [&>*]:shrink-0 [&>*]:grow [&>*]:basis-0">
      {children}
    </div>
  )
}
